import Alert from "@mui/material/Alert";
import {observer} from "mobx-react-lite";
import React, {useEffect} from "react";
import {useStores} from "../../../stores";
import {catchError, defer, from, repeat, retry, timer} from "rxjs";
import {environment} from "../../../env";
import {useRollbar} from "@rollbar/react";
import Button from "@mui/material/Button";

export const LoginPageAlert: React.FC = observer(() => {
    const {authStore, materializedApi, p} = useStores();
    const rollbar = environment.rollbarConfig ? useRollbar() : undefined;

    const [pingResult, setPingResult] = React.useState<'success' | 'error' | 'waiting' | null>(null);

    useEffect(() => {
        // Periodically ping the backend
        // This ensures the servers do not scale down
        const keepAlive = defer(() => from(materializedApi.getServerPing())).pipe(
            repeat({
                delay: environment.appEnginePinging.keepAliveInterval,
            }),
            catchError(error => {
                setPingResult('error');
                console.error('Ping failed', error);
                throw error;
            }),
            retry({
                count: environment.appEnginePinging.maxRetries,
                delay: (_, retryCount) =>
                    // Exponential backoff with a maximum interval
                    timer(Math.min((2 ^ retryCount) * 250, environment.appEnginePinging.retryInterval)),
                resetOnSuccess: true,
            }),
        ).subscribe({
            next: () => {
                setPingResult('success');
                console.info('Ping successful');
            },
            complete: () => {
                console.error('Give up ping');
            },
        });
        return () => keepAlive.unsubscribe()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (pingResult === null) {
            const noPingResponseTimeout = setTimeout(() => {
                // If there is no response after this amount of time, show the warning
                setPingResult('waiting');
            }, environment.appEnginePinging.retryInterval);
            return () => clearTimeout(noPingResponseTimeout);
        }
    }, [pingResult]);


    if (pingResult === 'error') {
        // Cannot connect, show that warning first
        return <Alert
            severity="error"
            action={p.p.enableBackendStatusPage && environment.statusPageUrl &&
                <Button href={environment.statusPageUrl} target="_blank" rel="noreferrer" color="inherit" size="small">
                    See status
                </Button>
            }
        >
            Could not reach the server
        </Alert>
    }
    if (pingResult === 'waiting') {
        return <Alert severity="warning">
            Connecting to the server, please wait a moment
        </Alert>
    }
    if (authStore.authentication.alert) {
        return <Alert severity={authStore.authentication.alert.severity}>
            {authStore.authentication.alert.msg}
        </Alert>
    }
    return <></>
});
